import { useMemo, useState } from 'react';
import type { Expense, ExpenseCategory, ExpenseStatus } from '@/lib/types';
import { CATEGORY_LABELS } from '@/lib/types';
import { filterExpenses, type ExpenseSearchFilters } from '@/application/expenses';
import { StatusBadge, CategoryChip } from '@/components/hearthline/primitives';
import { Icons } from '@/components/icons';
import { fmtUSD, fmtDateShort, fmtTime } from '@/lib/format';

interface Props {
  loading: boolean;
  expenses: Expense[];
  filters: ExpenseSearchFilters;
  onFiltersChange: (filters: ExpenseSearchFilters) => void;
  onSelectExpense: (expenseId: string) => void;
}

type SortKey = 'date-desc' | 'date-asc' | 'amt-desc' | 'amt-asc';
type RangeKey = 'all' | 'month' | '30d' | 'quarter' | 'ytd';

const STATUS_PILLS: { id: string; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'reimbursed', label: 'Reimbursed' },
  { id: 'donation', label: 'Donated' },
];

function withinRange(iso: string, range: RangeKey): boolean {
  if (range === 'all') return true;
  const d = new Date(iso);
  const now = new Date();
  if (range === '30d') return d >= new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  if (range === 'month') return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  if (range === 'ytd') return d.getFullYear() === now.getFullYear();
  if (range === 'quarter') {
    const q = Math.floor(now.getMonth() / 3);
    return d.getFullYear() === now.getFullYear() && Math.floor(d.getMonth() / 3) === q;
  }
  return true;
}

export function ExpenseList({ loading, expenses, filters, onFiltersChange, onSelectExpense }: Props) {
  const [sort, setSort] = useState<SortKey>('date-desc');
  const [range, setRange] = useState<RangeKey>('all');

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: expenses.length, pending: 0, reimbursed: 0, donation: 0 };
    for (const e of expenses) c[e.status] = (c[e.status] || 0) + 1;
    return c;
  }, [expenses]);

  const filtered = useMemo(() => {
    const list = filterExpenses(expenses, filters).filter((e) => withinRange(e.transaction_date, range));
    return [...list].sort((a, b) => {
      if (sort === 'date-desc') return a.transaction_date < b.transaction_date ? 1 : -1;
      if (sort === 'date-asc') return a.transaction_date > b.transaction_date ? 1 : -1;
      if (sort === 'amt-desc') return b.total - a.total;
      return a.total - b.total;
    });
  }, [expenses, filters, range, sort]);

  const sumFiltered = filtered.reduce((s, e) => s + e.total, 0);

  if (loading) return <div className="empty"><div className="ttl">Loading receipts…</div></div>;

  return (
    <>
      <div className="filters">
        {STATUS_PILLS.map((p) => (
          <button
            key={p.id}
            className={`filter-pill ${filters.status === p.id ? 'active' : ''}`}
            onClick={() => onFiltersChange({ ...filters, status: p.id })}
          >
            {p.label} <span className="ct">{counts[p.id] ?? 0}</span>
          </button>
        ))}

        <div className="filters-divider" aria-hidden="true" />

        <div className="filter-selects">
          <label className="select status-select">
            <span style={{ color: 'var(--muted)', fontSize: 11.5 }}>Status</span>
            <select value={filters.status} onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}>
              {STATUS_PILLS.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </label>

          <label className="select">
            <Icons.Filter size={13} stroke="var(--muted)" />
            <select value={filters.category} onChange={(e) => onFiltersChange({ ...filters, category: e.target.value })}>
              <option value="all">All categories</option>
              {(Object.keys(CATEGORY_LABELS) as ExpenseCategory[]).map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          </label>

          <label className="select">
            <Icons.Calendar size={13} stroke="var(--muted)" />
            <select value={range} onChange={(e) => setRange(e.target.value as RangeKey)}>
              <option value="all">All time</option>
              <option value="month">This month</option>
              <option value="30d">Last 30 days</option>
              <option value="quarter">This quarter</option>
              <option value="ytd">Year to date</option>
            </select>
          </label>

          <label className="select">
            <span style={{ color: 'var(--muted)', fontSize: 11.5 }}>Sort</span>
            <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              <option value="date-desc">Newest first</option>
              <option value="date-asc">Oldest first</option>
              <option value="amt-desc">Amount (high → low)</option>
              <option value="amt-asc">Amount (low → high)</option>
            </select>
          </label>
        </div>

        <div style={{ flex: 1 }} />

        <input
          className="expense-search"
          placeholder="Filter store or item…"
          value={filters.q}
          onChange={(e) => onFiltersChange({ ...filters, q: e.target.value })}
        />
      </div>

      <div className="card">
        <div className="card-h">
          <h3>{filtered.length} receipt{filtered.length !== 1 ? 's' : ''}</h3>
          <span className="sub">Total {fmtUSD(sumFiltered)}</span>
        </div>
        <div className="card-b tight">
          {filtered.length === 0 ? (
            <div className="empty">
              <Icons.Receipt size={26} stroke="var(--faint)" />
              <div className="ttl">No receipts match</div>
              <div>Try a different filter or clear search.</div>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Store</th>
                  <th>Items</th>
                  <th>Categories</th>
                  <th>Status</th>
                  <th className="num">Subtotal</th>
                  <th className="num">Tax</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const cats = [...new Set(r.items.map((i) => i.category))] as ExpenseCategory[];
                  return (
                    <tr key={r.id} onClick={() => onSelectExpense(r.id)}>
                      <td>
                        <div>{fmtDateShort(r.transaction_date)}</div>
                        <div className="dt">{fmtTime(r.transaction_date)}</div>
                      </td>
                      <td className="store">{r.store_name || 'Expense'}</td>
                      <td className="dt">{r.items.length} {r.items.length === 1 ? 'item' : 'items'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 240 }}>
                          {cats.slice(0, 3).map((c) => <CategoryChip key={c} category={c} />)}
                          {cats.length > 3 && <span className="chip">+{cats.length - 3}</span>}
                        </div>
                      </td>
                      <td><StatusBadge status={r.status as ExpenseStatus} /></td>
                      <td className="num dt">{fmtUSD(r.subtotal)}</td>
                      <td className="num dt">{fmtUSD(r.tax)}</td>
                      <td className="num" style={{ fontWeight: 500 }}>{fmtUSD(r.total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
